import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

/**
 * Privacy Policy Page
 * Comprehensive privacy policy covering all data collection, tracking, and user rights
 */
const PrivacyPolicy = () => {
  const currentYear = new Date().getFullYear();

  const sectionStyle = {
    marginBottom: '2rem'
  };

  const headingStyle = {
    marginBottom: '1rem',
    color: '#212529'
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Elliot Koss</title>
        <meta name="description" content="Privacy Policy for elliotkoss.com - how I collect, use, and protect your data" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Container style={{ paddingTop: '100px', paddingBottom: '2rem' }}>
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <h1 style={{ marginBottom: '2rem' }}>Privacy Policy</h1>
            <p className="text-muted mb-4"><strong>Effective Date:</strong> January 17, 2026</p>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>1. Introduction</h2>
              <p>
                This Privacy Policy describes how I, Elliot Koss, collect, use, share, and
                protect information when you use elliotkoss.com and its services (collectively,
                the "Services"). This is my personal portfolio website where I showcase my work
                and build new tools.
              </p>
              <p>
                I am committed to protecting your privacy and being transparent about my data
                practices. I do everything in my power to ensure that what I build is legal,
                compliant, and respectful of users' rights.
              </p>
              <p>
                By using my Services, you agree to the collection and use of information
                in accordance with this Privacy Policy. This policy should be read in
                conjunction with my <Link to="/terms-of-service">Terms of Service</Link>.
              </p>
              <p>
                If you have any concerns about my data practices, please reach out through the{' '}
                <Link to="/contact">contact form</Link> or connect with me on{' '}
                <a href="https://linkedin.com/in/elliotkoss" target="_blank" rel="noopener noreferrer">LinkedIn</a>.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>2. Information I Collect</h2>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1.5rem' }}>2.1 Information You Provide Directly</h3>
              <ul>
                <li><strong>Email Address:</strong> When you subscribe to my newsletter or contact me</li>
                <li><strong>Article URLs:</strong> When you use my Article to Media tool</li>
                <li><strong>Access Codes:</strong> If you use an access code for enhanced features</li>
                <li><strong>Contact Information:</strong> Name, email, and message content when you use my contact form</li>
                <li><strong>Feedback:</strong> When you report problems or provide feedback on generated content</li>
              </ul>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1.5rem' }}>2.2 Information Collected Automatically</h3>
              <p>When you visit my website, I automatically collect certain information:</p>
              <ul>
                <li><strong>Device Information:</strong> Browser type, operating system, device type, screen resolution</li>
                <li><strong>Usage Data:</strong> Pages visited, time spent on pages, click patterns, scroll depth</li>
                <li><strong>IP Address:</strong> I may collect and hash IP addresses for security and rate limiting (I do not store full IP addresses long-term)</li>
                <li><strong>Referral Data:</strong> How you arrived at my website (e.g., search engine, direct link)</li>
                <li><strong>Session Information:</strong> Session identifiers to manage your experience during a visit</li>
              </ul>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1.5rem' }}>2.3 Information from Third-Party Analytics</h3>
              <p>I use third-party analytics services that collect information about your use of my website:</p>
              <ul>
                <li><strong>Google Analytics:</strong> Collects anonymized usage data including pages visited, session duration, geographic region (country/city level), and traffic sources</li>
                <li><strong>Google Tag Manager:</strong> Manages and deploys tracking tags on my website</li>
                <li><strong>FullStory:</strong> Records session replays and interaction data to help me understand how users interact with my website, including mouse movements, clicks, and page navigation</li>
              </ul>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>3. How I Use Your Information</h2>
              <p>I use collected information for the following purposes:</p>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>3.1 Providing Services</h3>
              <ul>
                <li>Process your requests (e.g., converting articles to media)</li>
                <li>Deliver newsletters you have subscribed to</li>
                <li>Respond to your inquiries and support requests</li>
                <li>Manage access codes and usage limits</li>
              </ul>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>3.2 Improving My Services</h3>
              <ul>
                <li>Analyze usage patterns to improve user experience</li>
                <li>Identify and fix bugs and technical issues</li>
                <li>Develop new features and tools</li>
                <li>Understand which content and features are most valuable to users</li>
              </ul>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>3.3 Security and Operations</h3>
              <ul>
                <li>Prevent abuse, fraud, and unauthorized access</li>
                <li>Enforce usage limits and rate limiting</li>
                <li>Monitor for security threats</li>
                <li>Maintain and optimize my infrastructure</li>
              </ul>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>3.4 Communications</h3>
              <ul>
                <li>Send newsletters (if you subscribed)</li>
                <li>Send transactional emails related to your use of the Services</li>
                <li>Notify you of important changes to my Services or policies</li>
              </ul>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>4. Cookies and Tracking Technologies</h2>
              <p>I use cookies and similar tracking technologies to collect and store information:</p>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>4.1 Essential Cookies</h3>
              <p>Required for basic website functionality:</p>
              <ul>
                <li>Session management</li>
                <li>CAPTCHA verification</li>
                <li>Security and authentication</li>
              </ul>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>4.2 Analytics Cookies</h3>
              <p>Used to understand how visitors interact with my website:</p>
              <ul>
                <li>Google Analytics cookies (_ga, _gid, _gat)</li>
                <li>FullStory cookies for session recording</li>
              </ul>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>4.3 Managing Cookies</h3>
              <p>
                Most web browsers allow you to control cookies through their settings.
                You can typically find these settings in the "Options" or "Preferences"
                menu of your browser. Note that disabling certain cookies may affect
                the functionality of my Services.
              </p>
              <p>You can opt out of Google Analytics by installing the{' '}
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                  Google Analytics Opt-out Browser Add-on
                </a>.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>5. Data Storage and Retention</h2>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>5.1 Generated Content</h3>
              <p>
                When you use my tools, generated content (audio files, images, text summaries)
                is stored on my servers and accessible via unique URLs. This content may be
                indexed by search engines. Content is stored indefinitely unless:
              </p>
              <ul>
                <li>You request deletion through my reporting feature</li>
                <li>I determine removal is necessary for legal or operational reasons</li>
                <li>The content violates my Terms of Service</li>
              </ul>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>5.2 Newsletter Subscriptions</h3>
              <p>
                I retain your email address until you unsubscribe. You can unsubscribe
                at any time using the link in any newsletter email.
              </p>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>5.3 Analytics Data</h3>
              <p>
                Analytics data is retained according to the retention policies of each
                third-party provider (typically 14-26 months for Google Analytics).
              </p>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>5.4 Processing Logs</h3>
              <p>
                I retain logs of processing requests for analytics and debugging purposes.
                These logs are retained for a reasonable period necessary for operational purposes.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>6. Data Sharing and Disclosure</h2>
              <p><strong>I do not sell your personal information.</strong> I may share data in the following circumstances:</p>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>6.1 Service Providers</h3>
              <p>I use third-party services that process data on my behalf:</p>
              <ul>
                <li><strong>OpenAI:</strong> Processes content for AI-powered features (text, audio, images)</li>
                <li><strong>Amazon Web Services (AWS):</strong> Hosts my infrastructure and stores files</li>
                <li><strong>Google (Analytics, Tag Manager):</strong> Provides analytics services</li>
                <li><strong>FullStory:</strong> Provides session replay and analytics</li>
                <li><strong>Email Service Providers:</strong> Deliver newsletters and transactional emails</li>
              </ul>
              <p>
                These providers are contractually obligated to protect your information
                and use it only for the purposes I specify.
              </p>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>6.2 Legal Requirements</h3>
              <p>I may disclose information if required to:</p>
              <ul>
                <li>Comply with applicable laws, regulations, or legal processes</li>
                <li>Respond to lawful requests from public authorities</li>
                <li>Protect my rights, privacy, safety, or property</li>
                <li>Enforce my Terms of Service</li>
              </ul>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>6.3 Business Transfers</h3>
              <p>
                If this website is involved in a merger, acquisition, or sale of assets, your
                information may be transferred as part of that transaction.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>7. Public Content</h2>
              <p>
                Results generated by my tools are accessible via unique URLs. Important considerations:
              </p>
              <ul>
                <li>Generated result pages are not password-protected</li>
                <li>Anyone with the URL can view the content</li>
                <li>Result pages may be indexed by search engines</li>
                <li>Do not process sensitive, private, or confidential information</li>
              </ul>
              <p>
                If you need content removed, use the "Report a Problem" feature on the
                result page or contact me directly.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>8. Your Rights and Choices</h2>
              <p>Depending on your location, you may have the following rights:</p>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>8.1 Access and Portability</h3>
              <p>Request information about what personal data I have collected about you.</p>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>8.2 Correction</h3>
              <p>Request correction of inaccurate personal data.</p>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>8.3 Deletion</h3>
              <p>Request deletion of your personal data, subject to certain exceptions.</p>

              <h3 style={{ fontSize: '1.1rem', marginTop: '1rem' }}>8.4 Opt-Out</h3>
              <ul>
                <li><strong>Newsletter:</strong> Unsubscribe using the link in any email</li>
                <li><strong>Google Analytics:</strong> Use the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">opt-out browser add-on</a></li>
                <li><strong>Cookies:</strong> Manage through your browser settings</li>
              </ul>

              <p style={{ marginTop: '1rem' }}>
                To exercise these rights, please contact me through the{' '}
                <Link to="/contact">contact form</Link> or on{' '}
                <a href="https://linkedin.com/in/elliotkoss" target="_blank" rel="noopener noreferrer">LinkedIn</a>.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>9. Data Security</h2>
              <p>
                I implement reasonable security measures to protect your information:
              </p>
              <ul>
                <li>Encryption in transit (HTTPS/TLS)</li>
                <li>Secure cloud infrastructure with access controls</li>
                <li>Regular security assessments</li>
                <li>Limited access to personal data</li>
              </ul>
              <p>
                However, no method of transmission over the Internet or electronic storage
                is 100% secure. I cannot guarantee absolute security.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>10. International Data Transfers</h2>
              <p>
                My Services are hosted in the United States. If you are accessing the
                Services from outside the United States, please be aware that your
                information may be transferred to, stored, and processed in the United
                States where my servers are located.
              </p>
              <p>
                By using my Services, you consent to the transfer of your information
                to the United States.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>11. Children's Privacy</h2>
              <p>
                My Services are not directed to children under 13 (or under 16 in certain
                jurisdictions). I do not knowingly collect personal information from children.
                If you believe a child has provided me with personal information, please
                contact me immediately, and I will take steps to delete such information.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>12. California Privacy Rights</h2>
              <p>
                If you are a California resident, you may have additional rights under
                the California Consumer Privacy Act (CCPA), including:
              </p>
              <ul>
                <li>Right to know what personal information is collected</li>
                <li>Right to know whether personal information is sold or disclosed</li>
                <li>Right to say no to the sale of personal information (I do not sell personal information)</li>
                <li>Right to access your personal information</li>
                <li>Right to equal service and price</li>
              </ul>
              <p>
                To exercise these rights, please contact me through the{' '}
                <Link to="/contact">contact form</Link> or on{' '}
                <a href="https://linkedin.com/in/elliotkoss" target="_blank" rel="noopener noreferrer">LinkedIn</a>.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>13. Changes to This Policy</h2>
              <p>
                I may update this Privacy Policy from time to time. Changes will be
                posted on this page with an updated "Last Updated" date. Material changes
                may be communicated through the Services or via email to newsletter subscribers.
              </p>
              <p>
                Your continued use of my Services after changes constitutes acceptance
                of the updated Privacy Policy.
              </p>
            </section>

            <section style={sectionStyle}>
              <h2 style={headingStyle}>14. Contact</h2>
              <p>
                If you have questions about this Privacy Policy, my data practices, or
                wish to exercise your privacy rights, please reach out through the{' '}
                <Link to="/contact">contact form</Link> or connect with me on{' '}
                <a href="https://linkedin.com/in/elliotkoss" target="_blank" rel="noopener noreferrer">LinkedIn</a>.
              </p>
            </section>

            <div className="text-muted mt-4" style={{ fontSize: '0.875rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
              <p style={{ marginBottom: '0.5rem' }}>
                © 2025{currentYear > 2025 ? `–${currentYear}` : ''} Elliot Koss. All rights reserved.
              </p>
              <p style={{ fontStyle: 'italic', marginBottom: 0 }}>
                This document was drafted with the assistance of Claude Code, an AI tool.
                Claude Code is not a lawyer, and this does not constitute legal advice.
                If you notice anything that should be corrected, please{' '}
                <Link to="/contact" style={{ color: '#6c757d' }}>let me know</Link>.
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
